/**
 * The console's location, in the URL hash so every screen can be linked, refreshed and reached
 * with Back, with no server routing (the console is static files). Sections follow the sidebar's
 * jobs (docs/studio-console-redesign.md, section 2):
 *
 *   #/<brand>/home[?range=7|90]                      Home's dashboard, over the days shown
 *   #/<brand>/library/<type>[/<id>][?show=<filter>]   e.g. #/flowhouse/library/title/t-mf-day1
 *   …/<id>?field=<path>                               an item's editor, opened at one field
 *   #/<brand>/live[/<id>]                             live events and the schedule
 *   #/<brand>/app[/<id>]                              the app's screens
 *   #/<brand>/analytics[/<overview|content|audience|live|money>][?range=7|28|90]
 *   …/<id>?tab=analytics[&range=7]                    an item's editor, on its Analytics tab
 *   #/<brand>/pricing
 *   #/<brand>/review                                  Review & publish
 *   #/<brand>/settings/<brand|team|history|files|advanced>
 *
 * Links from before the redesign (#/<brand>/catalogue/title/x, #/<brand>/publish...) still land
 * on the matching screen.
 */

import { useEffect, useState } from "react";
import { BRANDS } from "@/api/client";
import { ENTITY_TYPES, type EntityType } from "@/api/admin";
import { DEFAULT_RANGE, parseRange, type RangeDays } from "@/analytics/range";

export const SECTIONS = ["home", "library", "live", "app", "analytics", "pricing", "review", "settings"] as const;
export type Section = (typeof SECTIONS)[number];
export const SETTINGS_PAGES = ["brand", "team", "history", "files", "advanced"] as const;
export type SettingsPage = (typeof SETTINGS_PAGES)[number];
export const ANALYTICS_TABS = ["overview", "content", "audience", "live", "money"] as const;
export type AnalyticsTab = (typeof ANALYTICS_TABS)[number];

export interface Route {
  brand: string;
  section: Section;
  /** Library: the kind listed or edited. Live and app imply theirs (liveEvent, page). */
  type?: EntityType;
  /** An item id, or "new" for a new one. */
  id?: string;
  page?: SettingsPage;
  /** A list filter (Library: draft, pending, live). */
  show?: string;
  /** An item's editor: the field to open (a path in the item, e.g. playback.video.videoId). */
  field?: string;
  /** Analytics: the tab shown (Overview when absent). */
  report?: AnalyticsTab;
  /** An item's editor: open on its Analytics tab. */
  tab?: "analytics";
  /** Home, Analytics, or an item's Analytics tab: the days shown (28 when absent). */
  range?: RangeDays;
}

const LEGACY: Record<string, Partial<Route>> = {
  dashboard: { section: "home" },
  publish: { section: "review" },
  media: { section: "settings", page: "files" },
  grants: { section: "pricing" },
  staff: { section: "settings", page: "team" },
  audit: { section: "settings", page: "history" },
};

const isType = (t: string | undefined): t is EntityType => (ENTITY_TYPES as readonly string[]).includes(t ?? "");

/** Where an item of this kind is listed and edited. */
export function itemRoute(brand: string, type: EntityType, id?: string): Route {
  if (type === "liveEvent") return { brand, section: "live", type, ...(id ? { id } : {}) };
  if (type === "page") return { brand, section: "app", type, ...(id ? { id } : {}) };
  return { brand, section: "library", type, ...(id ? { id } : {}) };
}

const LAST_BRAND = "viewform-console-brand";

/** The brand last opened, so the console's bare address opens it again. */
export function rememberBrand(id: string): void {
  try { localStorage.setItem(LAST_BRAND, id); } catch { /* the first brand opens instead */ }
}

function lastBrand(): string | undefined {
  try { const id = localStorage.getItem(LAST_BRAND); return BRANDS.some((b) => b.id === id) ? id! : undefined; } catch { return undefined; }
}

export function parseRoute(hash: string): Route {
  const [path = "", query = ""] = hash.replace(/^#\/?/, "").split("?");
  const [rawBrand, rawSection, ...rest] = path.split("/").map((p) => decodeURIComponent(p));
  const brand = BRANDS.some((b) => b.id === rawBrand) ? rawBrand! : lastBrand() ?? BRANDS[0]?.id ?? "";
  const params = new URLSearchParams(query);
  const show = params.get("show") ?? undefined;
  const field = params.get("field") ?? undefined;
  const range = params.has("range") ? parseRange(params.get("range")) : undefined;
  const withRange = (r: Route): Route => (range && range !== DEFAULT_RANGE ? { ...r, range } : r);
  const tab = params.get("tab") === "analytics" ? "analytics" as const : undefined;
  const withShow = (r: Route): Route => withRange({ ...r, ...(show ? { show } : {}), ...(field && r.id ? { field } : {}), ...(tab && r.id ? { tab } : {}) });

  if (rawSection === "catalogue") {
    const [type, ...id] = rest;
    return isType(type) ? itemRoute(brand, type, id.length ? id.join("/") : undefined) : { brand, section: "library", type: "title" };
  }
  if (rawSection && LEGACY[rawSection]) return { brand, section: "home", ...LEGACY[rawSection] } as Route;

  const section = (SECTIONS as readonly string[]).includes(rawSection ?? "") ? (rawSection as Section) : "home";
  switch (section) {
    case "library": {
      const [type, ...id] = rest;
      const known = isType(type) && type !== "liveEvent" && type !== "page" ? type : "title";
      return withShow({ brand, section, type: known, ...(isType(type) && id.length ? { id: id.join("/") } : {}) });
    }
    case "live":
      return withShow({ brand, section, type: "liveEvent", ...(rest.length && rest[0] ? { id: rest.join("/") } : {}) });
    case "app":
      return withShow({ brand, section, type: "page", ...(rest.length && rest[0] ? { id: rest.join("/") } : {}) });
    case "settings": {
      const page = (SETTINGS_PAGES as readonly string[]).includes(rest[0] ?? "") ? (rest[0] as SettingsPage) : "brand";
      return { brand, section, page };
    }
    case "analytics": {
      const report = (ANALYTICS_TABS as readonly string[]).includes(rest[0] ?? "") ? (rest[0] as AnalyticsTab) : "overview";
      return withRange({ brand, section, ...(report !== "overview" ? { report } : {}) });
    }
    case "home":
      return withRange({ brand, section });
    default:
      return { brand, section };
  }
}

export function href(route: Route): string {
  const parts: string[] = [route.brand, route.section];
  if (route.section === "library") parts.push(route.type ?? "title", ...(route.id ? [route.id] : []));
  else if ((route.section === "live" || route.section === "app") && route.id) parts.push(route.id);
  else if (route.section === "settings") parts.push(route.page ?? "brand");
  else if (route.section === "analytics" && route.report && route.report !== "overview") parts.push(route.report);
  const path = `#/${parts.map(encodeURIComponent).join("/")}`;
  const query = new URLSearchParams({
    ...(route.show ? { show: route.show } : {}),
    ...(route.field && route.id ? { field: route.field } : {}),
    ...(route.tab && route.id ? { tab: route.tab } : {}),
    ...(route.range && route.range !== DEFAULT_RANGE ? { range: String(route.range) } : {}),
  }).toString();
  return query ? `${path}?${query}` : path;
}

export function go(route: Route): void {
  window.location.hash = href(route);
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parseRoute(window.location.hash));
  useEffect(() => {
    const onChange = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return route;
}
